<template>
  <div>
    <Navbar />
    <Breadcrumb />
    <div class="mx-auto container-xl mb-5">
      <!-- Status indicator -->
      <!-- Survey information form, status and action buttons -->
      <div class="">
        <div class="title-area">
          <h1 class="page-title h2 mb-2">Survey: <span :class="`${anon ? 'g-anon-lg' : ''}`">{{ survey_name }}</span>
          </h1>
          <div class="text-md">
            <span>Group: {{ group_name }}</span>
          </div>
        </div>
        <div class="d-flex border-section justify-content-between">
          <div class="table-responsive d-table w-100 table-no-padding-first mb-5">
            <h3 class="mt-4">Survey information</h3>
            <table class="table first-td-500 table-alternative">
              <tr>
                <td>
                  {{ screentexts[72] ? screentexts[72].element_text : 'Survey name' }}
                </td>
                <td class="editable normal-input">
                  <b-form-input v-if="editable" v-model="survey.survey_name" :class="`${anon ? 'g-anon' : ''}`" />
                  <span v-else :class="`${anon ? 'g-anon' : ''}`">
                    {{ survey.survey_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[71] ? screentexts[72].element_text : 'Form type' }}
                </td>
                <td class="editable normal-input">
                  {{ surveytype_name }}
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[73] ? screentexts[73].element_text : 'Start date and time' }}
                </td>
                <td class="editable">
                  <div>
                    <b-form-group
                      v-if="editable && survey_status_id < 3"
                      class="d-flex"
                    >
                      <datetime
                        id="start_date"
                        v-model="survey.survey_start_date"
                        type="date"
                        input-class="form-control"
                        format="yyyy-LL-dd"
                        :min-datetime="new Date().toISOString()"
                        :value="survey.survey_start_date"
                        value-zone="Europe/Stockholm"
                        zone="Europe/Stockholm"
                        :phrases="{ ok: screentexts[60] ? screentexts[60].element_text : 'OK', cancel: screentexts[61] ? screentexts[61].element_text : 'Cancel' }"
                      />
                      <datetime
                        id="survey_start_time"
                        v-model="survey.survey_start_time"
                        type="time"
                        :value="survey.survey_start_time"
                        value-zone="Europe/Stockholm"
                        zone="Europe/Stockholm"
                        :minute-step="5"
                        input-class="form-control"
                        :phrases="{ ok: screentexts[60] ? screentexts[60].element_text : 'OK', cancel: screentexts[61] ? screentexts[61].element_text : 'Cancel' }"
                        :title="screentexts[62] ? screentexts[62].element_text : 'Select sending time'"
                        @input="checkstartdate"
                      />
                    </b-form-group>
                    <span v-else>
                      {{ this.survey_start_date > '1970-01-02' ? this.format_date(this.survey.survey_start_date) : 'Not set' }}
                      {{ this.survey_start_date > '1970-01-02' ? this.format_time(this.survey.survey_start_time) : '' }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[74] ? screentexts[74].element_text : 'End date and time' }}
                </td>
                <td class="editable">
                  <b-form-group v-if="editable && survey_status_id < 4">
                    <datetime
                      id="end_date"
                      v-model="survey.survey_end_date"
                      type="date"
                      input-class="form-control"
                      format="yyyy-LL-dd"
                      :min-datetime="new Date().toISOString()"
                      :value="survey.survey_end_date"
                      value-zone="Europe/Stockholm"
                      zone="Europe/Stockholm"
                      :phrases="{ ok: screentexts[60] ? screentexts[60].element_text : 'OK', cancel: screentexts[61] ? screentexts[61].element_text : 'Cancel' }" />
                  </b-form-group>
                  <span v-else>
                    {{ this.survey_start_date > '1970-01-02' ? this.format_date(survey.survey_end_date) : 'Not set' }}
                    {{ this.survey_start_date > '1970-01-02' ? '23:59' : '' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[75] ? screentexts[75].element_text : 'Language' }}
                </td>
                <td class="editable select-column">
                  <b-form-select v-if="editable" v-model="survey.survey_language_id" :options="language_options" />
                  <span v-else>
                    {{ language_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[76] ? screentexts[76].element_text : 'Norm data' }}
                </td>
                <td class="editable select-column">
                  <b-form-select v-if="editable" v-model="survey_normvalues_id" :options="norm_values_options" />
                  <span v-else>
                    {{ normvalues_country_name }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{ screentexts[77] ? screentexts[77].element_text : 'Reminder' }}
                </td>
                <td class="editable">
                  <b-form-checkbox v-if="editable && survey_status_id <= 3 && canremind" v-model="survey_reminder" :value="1" :unchecked-value="0"/>
                  <span v-else>
                    {{ survey_reminder == 1 ? 'Yes' : 'No' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  {{
                    screentexts[104]
                      ? screentexts[104].element_text
                      : "Reminder date"
                  }}
                </td>
                <td class="editable">
                  <b-form-group v-if="editable && survey_status_id <= 3 && canremind">
                    <datetime id="survey_reminder_date" v-model="survey_reminder_date" type="date"
                      input-class="form-control" format="yyyy-LL-dd"
                      :min-datetime="new Date(reminder_date_min).toISOString()" :phrases="{
                        ok: screentexts[99] ? screentexts[99].element_text : 'OK',
                        cancel: screentexts[100]
                          ? screentexts[100].element_text
                          : 'Cancel'
                      }"
                    />
                  </b-form-group>
                  <span v-else>{{ survey_reminder == 1 ? format_date(survey_reminder_date) : 'Not set' }}</span>
                </td>
              </tr>
              <tr>
                <td>
                  {{
                    screentexts[107]
                      ? screentexts[107].element_text
                      : "Reminder email"
                  }}
                </td>
                <td class="editable normal-input">
                  <b-form-input v-if="editable && survey_status_id <= 3 && canremind" v-model="survey_reminder_email" :class="`${anon ? 'g-anon' : ''}`"
                    :placeholder="screentexts[108]
                      ? screentexts[108].element_text
                      : 'Enter email address'
                      " />
                  <span v-else>{{ survey_reminder_email }}</span>
                </td>
              </tr>
            </table>
            <b-button :disabled="saving" variant="secondary" class="btn-small mt-3" @click="toggleEditable">
              {{ editable ? 'Save' : 'Edit' }}
            </b-button>
          </div>
          <div class="text-center alert-section">
            <div class="mt-4">
              <div v-if="survey_status_id == 2" class="bg-warning alert">
                {{ screentexts[90] ? screentexts[90].element_text : "Scheduled: " }}
                {{ format_date(survey_start_date) }}
              </div>
              <div v-if="survey_status_id == 3" class="bg-secondary alert">
                {{
                  survey.survey_end_date > '1970-01-02' ?
                  `${screentexts[91] ? screentexts[91].element_text : "Active until: "} ${ format_date(survey_end_date) }` :
                  `Incorrect survey status`
                }}
              </div>
              <div   v-if="survey_status_id == 4" class="bg-primary alert">
                {{
                  screentexts[92] ? screentexts[92].element_text : "Completed"
                }}
              </div>
            </div>
            <div class="d-none d-xl-inline-block">
              <Chart :sub="completedGroupMembers.length" :total="includedGroupMembers.length" />
            </div>
            <br>
            <!-- Print paper forms, only if survey has paper -->
            <div v-show="has_paper == 1" class="col-12 text-center">
              <b-button inline
                class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2 d-none d-lg-inline-block"
                variant="primary" @click.prevent="print_paperforms">
                {{
                  screentexts[113]
                    ? screentexts[113].element_text
                    : "Print all paper forms"
                }}
              </b-button>
              <br>
            </div>
            <!-- Status 1 = Draft -->
            <!-- Status 2 = Ready to launch -->
            <div v-if="survey_status_id < 3" class="col-12 mx-auto">
              <b-button inline class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                :disabled="survey_status_id < 2" variant="primary text-center" @click.prevent="groupsurvey_launch">
                {{
                  screentexts[114]
                    ? screentexts[114].element_text
                    : "Launch survey now!"
                }}
              </b-button>
            </div>
            <!-- Status 3 = Active -->
            <div v-if="survey_status_id == 3 && completecount > 0" class="col-12 mx-auto">
              <b-button inline class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                variant="danger text-center" @click.prevent="groupsurvey_close_ask">
                {{
                  screentexts[115]
                    ? screentexts[115].element_text
                    : "Close survey"
                }}
              </b-button>
              <br>
            </div>
            <!-- Status 4 = Completed -->
            <div v-if="survey_status_id == 4" class="col-12 mx-auto">
              <b-button inline variant="secondary text-center"
                class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2"
                @click.prevent="groupsurvey_reopen">
                {{
                  screentexts[116]
                    ? screentexts[116].element_text
                    : "Re-open survey"
                }}
              </b-button>
              <br>
              <b-button v-if="spotify === '0'" inline
                class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2" variant="primary text-center"
                @click.prevent="groupsurvey_report">
                {{
                  screentexts[117]
                    ? screentexts[117].element_text
                    : "Download report"
                }}
              </b-button>
              <b-button v-if="spotify === '1'" inline
                class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2" variant="primary text-center"
                @click.prevent="groupsurvey_report">
                Download Spotify report
              </b-button>
              <br>
            </div>
            <div v-if="asadmin" class="col-12 mx-auto">
              <b-button v-b-toggle.collapse-1 inline
                class="text-center col-12 col-sm-8 col-md-6 col-lg-5 col-xl-12 my-2" variant="danger">
                <fa-icon class="mr-4" name="skull-crossbones" />
                Show admin functions
              </b-button>
            </div>
          </div>
        </div>
        <!--b-card v-if="asadmin==1" class="border-0 mx-auto col-12 pl-0 px-0" -->
        <b-collapse id="collapse-1" class="border-0 mx-auto col-12 pl-0 px-0">
          <b-card class="px-0 my-2 mx-auto col-12">
            <b-button v-if="survey_type_id == 1" class="mr-2 px-2" @click.prevent="admin_excluded">
              Set optional questions
            </b-button>
            <b-button class="mr-2 px-2" @click.prevent="admin_mailtext">
              Change mail subject/text
            </b-button>
            <b-button class="mr-2 px-2" @click.prevent="admin_reimport">
              Re-import answers
            </b-button>
            <b-button class="mr-2 px-2" @click.prevent="admin_addon">
              Add-on Survey

            </b-button>
            <div v-if="administer_excluded" class="py-4">
              <h5>Optional questions</h5>
              <b-row v-for="excludablequestion in excludablequestions" :key="excludablequestion.generalquestion_id"
                class="px-0 mx-0 py-0 my-0">
                <b-form-checkbox :id="excludablequestion.generalquestion_id" v-model="excludedquestions"
                  :value="excludablequestion.generalquestion_id" button button-variant="white">
                  <fa-icon v-if="!excludedquestions.includes(excludablequestion.generalquestion_id)" name="times"
                    class="mr-1" />
                  <fa-icon v-if="excludedquestions.includes(excludablequestion.generalquestion_id)" name="plus"
                    class="mr-1 mb-1 mt-0 pt-0" />
                  <span class="mt-1"
                    :class="excludedquestions.includes(excludablequestion.generalquestion_id) ? 'gdq-strike' : ''">
                    {{ excludablequestion.generalquestion_text }}
                  </span>
                </b-form-checkbox>
              </b-row>
              <b-button variant="primary" @click.prevent="admin_excluded_save">
                Save optional questions
              </b-button>
            </div>
            <div v-if="administer_mailtext" class="py-4">
              <h5>Mailtexts</h5>
              <b-form-group :label="'Invitation E-mail Subject Line'">
                <b-form-input v-model="survey_invitation_subject" class="col-12" />
              </b-form-group>
              <b-form-group :label="'Invitation E-mail Personal Text'">
                <vue-editor v-model="survey_invitation_consultant_text" :editor-toolbar="editortoolbar" />
              </b-form-group>
              <b-button variant="primary" @click.prevent="admin_mailtext_save">
                Save mail text
              </b-button>
            </div>
            <div v-if="administer_reimport" class="py-4">
              <b-alert v-model="reimportstatus" :variant="reimportstatus == 2 ? 'danger' : 'success'" dismissible>
                <span v-if="reimportstatus == 2">Data for import was not found</span>
                <span v-if="reimportstatus == 1">Import done.</span>
              </b-alert>
              <h5>Re-import answers</h5>
              This function will attempt to re-import the survey answers (GDQ and general) from the old system. You can
              run this function more than once for a survey without doing any damage.
              <div v-show="reimporting > 0" class="row mx-1">
                <h5>
                  Re-importing {{ reimporting }} of {{ $store.state.currentsurveymembers.length }}
                </h5>
              </div>
              <div v-show="reimporting == -1" class="row">
                <b-button class="mt-2 mx-3" variant="primary" @click.prevent="reimport">
                  Re-import answers
                </b-button>
                <b-button class="mt-2 mx-3" variant="danger" @click.prevent="admin_reimport">
                  Cancel
                </b-button>
              </div>
            </div>
            <div v-if="administer_addon" class="py-4">
              <b-select v-model="addon_survey" :options="addonsurveys"
                @change="savefield('addon_survey', addon_survey)" />
            </div>
          </b-card>
        </b-collapse>
        <!-- /b-card -->
      </div>

      <div class="mt-5">
        <div>
          <!-- b-tabs content-class="mt-3" -->
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.accordion-1 variant="info" id="scrollto">
                  {{
                    screentexts[118]
                      ? screentexts[118].element_text
                      : "Survey participants"
                  }}
                </b-button>
              </b-card-header>
            </div>
            <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
              <div class="d-flex justify-content-start mt-3">
                <b-button v-show="survey_status_id < 4" class="mr-3" variant="primary" @click.prevent="toggleaddmember">
                  {{
                    screentexts[119]
                      ? screentexts[119].element_text
                      : "Add survey participant"
                  }}
                </b-button>
                <!-- Contact members -->
                <b-button v-show="numberSelectedMembersWithEmail > 0" :disabled="membersselected.length == 0" class="mr-3" variant="secondary"
                  @click.prevent="togglecontactmembers">
                  {{
                    screentexts[120] ? screentexts[120].element_text : "Contact"
                  }}
                  {{ numberSelectedMembersWithEmail }}
                  {{
                    screentexts[121] ? screentexts[121].element_text : "member"
                  }}<span v-if="
                    numberSelectedMembersWithEmail > 1 || numberSelectedMembersWithEmail == 0
                  ">s</span>
                </b-button>
                <b-button v-show="survey_status_id < 4 && numberSelectedMembersWithEmail > 0" :disabled="membersselected.length == 0" class="" variant="warning"
                  @click.prevent="togglesendinvitations">
                  {{
                    screentexts[122] ? screentexts[122].element_text : "Send"
                  }}
                  {{ numberSelectedMembersWithEmail }}
                  {{
                    screentexts[123]
                      ? screentexts[123].element_text
                      : "invitation"
                  }}<span v-if="
                    numberSelectedMembersWithEmail > 1 || numberSelectedMembersWithEmail == 0
                  ">s</span>
                </b-button>
              </div>
              <b-row>&nbsp;</b-row>
              <div v-show="contactmembers == true">
                <h4>
                  {{
                    screentexts[124]
                      ? screentexts[124].element_text
                      : "Contact Members"
                  }}
                  {{
                    screentexts[129]
                      ? screentexts[129].element_text
                      : "email"
                  }}<span v-if="numberSelectedMembersWithEmail > 1">s</span>
                </h4>
                <span v-if="numberSelectedMembersWithEmail != membersselected.length">
                  Please note: Some selected members do not have an email address and will not be contacted using this function
                </span>
                <b-form novalidate @submit.stop.prevent class="bg-secondary add-member-container">
                  <b-form-group>
                    <label class="pl-0">
                      {{ screentexts[131]
                        ? screentexts[131].element_text
                        : 'Email subject' }}
                    </label>
                    <b-form-input v-model="mail_subject" :placeholder="screentexts[125]
                      ? screentexts[125].element_text
                      : 'Email subject'
                      " />
                  </b-form-group>
                  <b-form-group :label="screentexts[126]
                    ? screentexts[126].element_text
                    : 'Email text'
                    ">
                    <vue-editor v-model="mail_consultant_text" :editor-toolbar="editortoolbar" />
                  </b-form-group>
                  <div class="text-right">
                    <b-button variant="danger" class="mr-3" @click.prevent="togglecontactmembers">
                      <span class="h4" />{{
                        screentexts[127]
                          ? screentexts[127].element_text
                          : "Cancel"
                      }}
                    </b-button>
                    <b-button variant="primary" class="" @click.prevent="sendemails('Other')">
                      {{
                        screentexts[128]
                          ? screentexts[128].element_text
                          : "Send"
                      }}
                      {{ membersselected.length }}
                      {{
                        screentexts[129]
                          ? screentexts[129].element_text
                          : "email"
                      }}<span v-if="membersselected.length > 1">s</span>
                    </b-button>
                  </div>
                  <b-row class="h4">
                    &nbsp;<br>&nbsp;
                  </b-row>
                </b-form>
              </div>
              <div v-show="sendinvitations == true">
                <h4>
                  {{
                    screentexts[130]
                      ? screentexts[130].element_text
                      : "Send Invitations"
                  }}
                </h4>
                <span v-if="numberSelectedMembersWithEmail != membersselected.length">
                  Please note: Some selected members do not have an email address and will not be sent the invitation using this function
                </span>
                <b-form novalidate @submit.stop.prevent class="bg-secondary add-member-container">
                  <b-form-group>
                    <label class="pl-0">
                      {{ screentexts[131]
                        ? screentexts[131].element_text
                        : 'Email subject' }}
                    </label>
                    <b-form-input v-model="survey_invitation_subject" :placeholder="screentexts[131]
                      ? screentexts[131].element_text
                      : 'Email subject'
                      " @change="
                        savefield(
                          'survey_invitation_subject',
                          survey_invitation_subject
                        )
                        " />
                  </b-form-group>
                  <b-form-group v-if="survey.spotify != 1"
                    :label="screentexts[155] ? screentexts[155].element_text : 'Invitation e-mail fixed text'">
                    <div class="text-container">
                      <div id="fixed-text" class="mt-3"
                        v-html="this.$store.state.currentsurvey.survey_invitation_text" />
                      <div class="d-flex justify-content-end mb-4">
                        <b-button variant="primary" @click="copyText" class="btn-small">
                          Copy Text
                        </b-button>
                      </div>
                    </div>
                  </b-form-group>
                  <b-form-group>
                    <label>
                      <span>{{
                        screentexts[17] ? screentexts[17].element_text : ""
                        }}</span>
                      <!-- <PopupBubble
                        v-if="screentexts[17] && screentexts[17].element_bubbletext != '' && screentexts[17].element_bubbletext !== null"
                        element_id="invitation-email-personal-text"
                        :screen_id="screen_id"
                        screenelement_id="17"
                        :bubbletext="
                          screentexts[17]
                            ? screentexts[17].element_bubbletext
                            : ''
                        "
                      /> -->
                    </label>
                    <vue-editor id="survey_invitation_consultant_text" v-model="survey_invitation_consultant_text"
                      :editor-toolbar="editortoolbar" @blur="
                        savefield(
                          'survey_invitation_consultant_text',
                          survey_invitation_consultant_text
                        )
                        " />
                  </b-form-group>
                  <b-form-group>
                    <label>
                      {{ screentexts[132]
                        ? screentexts[132].element_text
                        : 'Email replies to' }}
                    </label>
                    <b-form-input v-model="survey_invitation_cc" :placeholder="screentexts[133]
                      ? screentexts[133].element_text
                      : 'Email reply address'
                      " @change="
                        savefield('survey_invitation_cc', survey_invitation_cc)
                        " />
                  </b-form-group>
                  <div class="text-right">
                    <b-button variant="danger" class="mr-3" @click.prevent="togglesendinvitations">
                      <span class="h4" />{{
                        screentexts[134]
                          ? screentexts[134].element_text
                          : "Cancel"
                      }}
                    </b-button>
                    <b-button variant="primary" class="" @click.prevent="sendemails('Invitation')">
                      {{
                        screentexts[135]
                          ? screentexts[135].element_text
                          : "Send"
                      }}
                      {{ membersselected.length }}
                      {{
                        screentexts[136]
                          ? screentexts[136].element_text
                          : "Invitation"
                      }}<span v-if="membersselected.length > 1">s</span>
                    </b-button>
                  </div>
                  <b-row class="h4">
                    &nbsp;<br>&nbsp;
                  </b-row>
                </b-form>
              </div>
              <div v-if="addmember">
                <h4>
                  {{
                    screentexts[119]
                      ? screentexts[119].element_text
                      : "Add survey participant"
                  }}
                </h4>
                <b-form class="bg-secondary add-member-container" novalidate @submit.stop.prevent>
                  <b-form-group class="">
                    <label class="">
                      {{ screentexts[167] ? screentexts[167].element_text : 'Form Type' }}
                    </label>
                    <b-form-select v-model="groupmember_formoption_id" :options="formoptions" />
                  </b-form-group>
                  <b-form-group :state="groupmember_email_valid" v-show="groupmember_formoption_id == 1" class="">
                    <label class="col-12 pl-0">
                      {{ screentexts[164] ? screentexts[164].element_text : 'Email address' }}
                    </label>
                    <input v-model="groupmember_email" class="form-control col-12" :class="`${anon ? 'g-anon' : ''}`"
                      :state="groupmember_email_valid" type="text"
                      :placeholder="screentexts[170] ? screentexts[170].element_text : 'Email Address'">
                    <span v-show="groupmember_email_valid == 'invalid'" class="text-danger">Invalid or duplicate email
                      address</span>
                    <span v-show="groupmember_email_valid != 'invalid'">&nbsp;</span>
                  </b-form-group>
                  <b-form-group class="button-group mb-0">
                    <div class="text-right">
                      <b-button variant="primary" @click.prevent="addgroupmember">
                        <span class="d-inline-block d-md-none d-lg-inline-block">{{
                          screentexts[175] ? screentexts[175].element_text : 'Add' }}</span>
                      </b-button>
                      <b-button variant="danger" class="ml-2" @click="toggleaddmember">
                        <span class="d-inline-block d-md-none d-lg-inline-block">Cancel</span>
                      </b-button>
                    </div>
                  </b-form-group>
                </b-form>
              </div>
              <b-table :fields="fields" :class="'actions-table save-cancel'" :items="includedGroupMembers"
                :tbody-tr-class="rowClass">
                <template v-slot:head(select)>
                  <b-form-checkbox v-model="selected" @click.native.stop @change="selectall(selected)" />
                </template>
                <template v-slot:cell(select)="data">
                  <div>
                    <b-form-checkbox v-if="data.item.included_in_survey == 1" v-model="data.item.selected"
                      @input="countselected" />
                  </div>
                </template>
                <template v-slot:cell(actions)="data">
                  <div class="d-flex justify-content-end">
                    <div v-if="data.item.editable">
                      <b-button variant="danger" class="btn-tiny mr-1"
                        @click.prevent="cancelEdit(data.item.id)">Cancel</b-button>
                      <b-button variant="primary" class="btn-tiny"
                        @click.prevent="saveEdit(data.item.id)">Save</b-button>
                    </div>
                    <b-dropdown right class="ml-2">
                      <template #button-content>
                        <i class="fa-solid fa-ellipsis"></i>
                      </template>
                      <b-dropdown-item @click.prevent="editGroupMember(data.item.id)">Edit</b-dropdown-item>
                      <b-dropdown-item v-if="canExcludeMembers" @click.prevent="togglegroupmember(data.item.id)">Exclude from this
                        survey</b-dropdown-item>
                      <b-dropdown-item v-if="data.item.groupsurveymember_formoption_id != 2"
                        @click.prevent="getlink(data.item.id)">
                        Copy invitation
                      </b-dropdown-item>
                      <b-dropdown-item v-if="data.item.groupsurveymember_formoption_id == 2"
                        @click.prevent="printform(data.item.id)">
                        Print form
                      </b-dropdown-item>
                      <b-dropdown-item v-if="data.item.groupsurveymember_formoption_id == 2"
                        @click.prevent="openquestionnaire(data.item.id)">
                        Open survey
  §                    </b-dropdown-item>
                      <b-dropdown-item v-if="data.item.groupsurveymember_formoption_id == 1 && survey.survey_status_id < 4" @click.prevent="toggleSelected(data.item.id, 'scrollto', 'invite');">
                        Send invitation
                      </b-dropdown-item>
                      <b-dropdown-item v-if="data.item.groupsurveymember_formoption_id == 1" @click.prevent="toggleSelected(data.item.id, 'scrollto', 'contact');">
                        Contact member
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </template>

                <template v-slot:cell(groupsurveymember_status_id)="data">
                  <div v-show="data.item.included_in_survey == 1">
                    <div v-if="data.item.groupsurveymember_status_id == 1" class="bg-danger">
                      <span class="d-inline d-sm-none" :class="data.item.displayclass">{{
                        data.item.groupsurveymember_survey_code }}</span>
                      <span class="d-none d-md-inline">
                        <span v-if="data.item.groupsurveymember_formoption_id == 1">Not sent</span>
                        <span v-if="data.item.groupsurveymember_formoption_id == 2">Not printed</span>
                        <span v-if="data.item.groupsurveymember_formoption_id == 3">Manual send</span>
                      </span>
                    </div>
                    <div v-if="data.item.groupsurveymember_status_id == 2" class="bg-none">
                      <span class="d-inline d-sm-none" :class="data.item.displayclass">{{
                        data.item.groupsurveymember_survey_code }}</span>
                      <span class="d-none d-md-inline">
                        <span v-if="data.item.groupsurveymember_formoption_id == 1">Sent</span>
                        <span v-if="data.item.groupsurveymember_formoption_id == 2">Printed</span>
                        <span v-if="data.item.groupsurveymember_formoption_id == 3">Manual send</span>
                      </span>
                    </div>
                    <div v-if="data.item.groupsurveymember_status_id == 3" class="bg-secondary">
                      <span class="d-inline d-sm-none" :class="data.item.displayclass">{{
                        data.item.groupsurveymember_survey_code }}</span>
                      <span class="d-none d-md-inline">Started (<span class="d-none d-xl-inline">{{
                        data.item.gdq_answers }})</span></span>
                    </div>
                    <div v-if="data.item.groupsurveymember_status_id == 4" class="bg-primary">
                      <span class="d-inline d-sm-none" :class="data.item.displayclass">{{
                        data.item.groupsurveymember_survey_code }}</span>
                      <span class="d-none d-md-inline">Completed (<span class="d-none d-xl-inline">{{
                        data.item.gdq_answers }})</span></span>
                    </div>
                  </div>
                  <div v-show="data.item.included_in_survey == 0">
                    <div class="text-muted col-12 small pt-2">
                      <span class="d-none d--inline">Not participating</span>
                    </div>
                  </div>
                </template>

                <template v-slot:cell(groupsurveymember_survey_code)="data">
                  {{ data.item.groupsurveymember_survey_code }}
                </template>

                <template v-slot:cell(groupmember_email)="data">
                  <div class="position-relative">
                    <div class="uneditable" v-if="!data.item.editable" :class="{ 'g-anon': anon }">{{ data.item.groupmember_email }}</div>
                    <b-form-input
                        v-else
                        v-model="data.item.groupmember_email"
                        type="text"
                        :class="{'g-anon': anon, 'text-danger': data.item.groupmember_email_valid == 'invalid'}"
                      />
                      <i
                        v-if="data.item.groupmember_email_valid == 'invalid' && data.item.editable"
                        class="fa-solid inline-tooltip fa-circle-question tooltip-question tooltip-danger"
                        v-b-tooltip="'Invalid email address'"
                      ></i>
                  </div>
                </template>

                <!-- <template v-slot:cell(groupmember_name)="data">
            <b-form-input
              v-model="data.item.groupmember_name"
              type="text"
            />
          </template> -->
                <template v-slot:cell(groupmember_mobile)="data">
                  <b-form-input v-model="data.item.groupmember_mobile" type="text" />
                </template>

                <template v-slot:cell(groupsurveymember_formoption_id)="data">
                  <div class="uneditable" v-if="!data.item.editable">{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                  <b-form-select v-else-if="data.item.groupsurveymember_status_id == 1"
                    v-model="data.item.groupsurveymember_formoption_id" :options="formoptions" />
                  <div class="uneditable" v-else>{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                </template>

              </b-table>
              <!-- <GroupSurveyMembers mode="edit" /> -->
              <h3 class="mt-4">
                Members not included in survey
              </h3>
              <b-table :fields="fieldsExclude" :class="'actions-table save-cancel excluded mb-5'"
                :items="excludedGroupMembers" :tbody-tr-class="rowClass">
                <template v-slot:cell(actions)="data">
                  <div class="d-flex justify-content-end">
                    <div v-if="data.item.editable">
                      <b-button variant="danger" class="btn-tiny mr-1"
                        @click.prevent="cancelEdit(data.item.id)">Cancel</b-button>
                      <b-button variant="primary" class="btn-tiny"
                        @click.prevent="saveEdit(data.item.id)">Save</b-button>
                    </div>
                    <b-dropdown right class="ml-2">
                      <template #button-content>
                        <i class="fa-solid fa-ellipsis"></i>
                      </template>
                      <b-dropdown-item @click.prevent="editGroupMember(data.item.id)">Edit</b-dropdown-item>
                      <b-dropdown-item v-if="canExcludeMembers  || data.item.groupsurveymember_status_id <  4" @click.prevent="togglegroupmember(data.item.id)">Include in this
                        survey</b-dropdown-item>
                    </b-dropdown>
                  </div>
                </template>

                <template v-slot:cell(groupmember_code)="data">
                  {{ data.item.groupmember_code }}
                </template>

                <template v-slot:cell(groupmember_email)="data">
                  <div>
                    <div class="uneditable" v-if="!data.item.editable" :class="{ 'g-anon': anon }">{{
                      data.item.groupmember_email }}</div>
                    <b-form-input v-else v-model="data.item.groupmember_email" type="text"
                      :class="{ 'g-anon': anon }" />
                  </div>
                </template>

                <template v-slot:cell(groupmember_mobile)="data">
                  <b-form-input v-model="data.item.groupmember_mobile" type="text" />
                </template>

                <template v-slot:cell(groupsurveymember_formoption_id)="data">
                  <div class="uneditable" v-if="!data.item.editable">{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                  <b-form-select v-else-if="data.item.groupsurveymember_status_id == 1"
                    v-model="data.item.groupsurveymember_formoption_id" :options="formoptions" />
                  <div class="uneditable" v-else>{{
                    getFormOptionText(data.item.groupsurveymember_formoption_id) }}</div>
                </template>

              </b-table>

            </b-collapse>
          </div>
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.accordion-2 variant="info">
                  Scheduled emails
                </b-button>
              </b-card-header>
            </div>
            <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
              <GroupSurveyScheduledMails :surveyid="survey_id" :changed="surveychanged" status="scheduled" />
            </b-collapse>
          </div>
          <div class="accordion full-width" role="tablist">
            <div class="card">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.accordion-3 variant="info">
                  Sent emails
                </b-button>
              </b-card-header>
            </div>
            <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
              <GroupSurveyScheduledMails :surveyid="survey_id" :changed="surveychanged" status="sent" />
            </b-collapse>
          </div>
        </div>
      </div>
    </div>
    <b-modal v-model="wanttoclose" hide-footer title="Close survey?">
      When you close the survey, participants can no longer fill in answers, and you can download the survey report
      <span v-if="anonymize_survey_on_close == 1">
        <p></p>
        <p class="text-danger"><strong>All personal information in the survey will be anonymized. This cannot be undone.</strong></p>
        <b-form-checkbox v-model="confirmAnonymize" class="mt-2">
          I understand that all personal data will be permanently deleted
        </b-form-checkbox>
      </span>
      <b-button class="mt-3" :variant="anonymize_survey_on_close == 1 ? 'danger' : 'primary'" block
        :disabled="anonymize_survey_on_close == 1 && !confirmAnonymize"
        @click="groupsurvey_close">
        Yes, close
      </b-button>
      <b-button class="mt-2" variant="secondary" block @click="groupsurvey_close_cancel">
        Cancel
      </b-button>
    </b-modal>
    <b-modal id="memberlink" size="lg" title="Questionnaire link and personal code">
      <b-form-group>
        <b-form-textarea v-model="currentcodelink" rows="18" readonly />
      </b-form-group>
    </b-modal>
    <b-modal
      id="adding-member-modal"
      :visible="addingMember"
      hide-footer
      hide-header
      centered
      no-close-on-backdrop
      no-close-on-esc
      static
    >
      <div class="text-center p-4">
        <b-spinner class="mb-3" variant="primary" />
        <div>Adding member...</div>
      </div>
    </b-modal>    
  </div>
</template>
<script>
import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'
import GroupSurveyMembers from './GroupSurveyMembers.vue'
// import GroupSurveyLog from "./GroupSurveyLog.vue";
// import PopupBubble from "./PopupBubble.vue";
import { Datetime } from 'vue-datetime'
import { VueEditor } from 'vue2-editor'
import { config } from '../../config'
import GroupSurveyScheduledMails from './GroupSurveyScheduledMails.vue'
import Chart from './styleguide/Chart.vue'

var editortools = [
  // [{ font: [] }],
  [{ header: [false, 1, 2, 3, 4, 5, 6] }],
  //  [{ size: ["small", false, "large", "huge"] }],
  //  ["bold", "italic", "underline", "strike"],
  ['bold', 'italic'],
  [
    { align: '' },
    { align: 'center' },
    { align: 'right' },
    { align: 'justify' }
  ],
  //  [{ header: 1 }, { header: 2 }],
  //  ["blockquote", "code-block"],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  //  [{ script: "sub" }, { script: "super" }],
  //  [{ indent: "-1" }, { indent: "+1" }],
  //  [{ color: [] }, { background: [] }],
  [{ color: [] }],
  //  ["link", "image", "video", "formula"],
  ['image']
  //  [{ direction: "rtl" }],
  //  ["clean"]
]

export default {
  name: 'GroupSurveyPage',
  components: {
    Navbar,
    Breadcrumb,
    GroupSurveyMembers,
    //    GroupSurveyLog,
    Chart,
    Datetime,
    VueEditor,
    GroupSurveyScheduledMails
    // PopupBubble
  },
  data () {
    return {
      addingmember: false,
      isAddMemberVisible: false,
      loading: true,
      saving: false,
      editortoolbar: editortools,
      group_name: this.$store.state.currentgroup.group_name,
      surveytype_name: 'GDQ',
      survey: this.$store.state.currentsurvey,
      survey_name: null,
      survey_name_valid: null,
      survey_normvalues_id: null,
      survey_normvalues_id_valid: null,
      survey_type_id: null,
      survey_status_id: null,
      survey_status_id_valid: null,
      survey_invoice_reference: null,
      survey_start_date: null,
      survey_start_date_valid: null,
      survey_start_time: null,
      survey_end_date: null,
      survey_end_date_valid: null,
      survey_reminder: null,
      survey_reminder_date: null,
      survey_reminder_date_valid: null,
      survey_reminder_time: null,
      survey_reminder_time_valid: null,
      survey_reminder_email: null,
      survey_reminder_email_valid: null,
      survey_invitation_subject: null,
      survey_invitation_text: this.$store.state.currentsurvey.survey_invitation_text,
      survey_invitation_consultant_text: this.$store.state.currentsurvey.survey_invitation_consultant_text,
      survey_invitation_cc: null,
      addon_survey: 0,
      addmember: false,
      contactmembers: false,
      sendinvitations: false,
      groupmember_name: '',
      groupmember_name_valid: null,
      groupmember_email: '',
      groupmember_email_valid: null,
      groupmember_mobile: '',
      groupmember_mobile_valid: null,
      groupmember_formoption_id: 1,
      groupsurveymember_formoption_id: 1,
      groupmembers_grid: [],
      nextid: 10000000,
      formchanged: false,
      mail_subject: null,
      mail_consultant_text: '',
      mail_cc: null,
      mail_text: this.$store.state.currentsurvey.survey_invitation_text,
      labels: [],
      addinglabel: 0,
      addinglabeltext: '',
      has_online: 0,
      has_paper: 0,
      screen_id: 7,
      screentexts: [],
      asadmin: this.$store.state.asadmin,
      administer_excluded: false,
      excludablequestions: [],
      excludedquestions: [],
      administer_mailtext: false,
      administer_reimport: false,
      administer_addon: false,
      language_options: [],
      norm_values_options: [],
      surveychanged: false,
      reimporting: -1,
      reimported: [],
      reimportstatus: 0,
      addonsurveys: [],
      anonymize_survey_on_close: this.$store.state.profile.anonymize_survey_on_close,
      wanttoclose: false,
      confirmAnonymize: false,
      editable: false,
      languages: [],
      profile: this.$store.state.profile,
      spotify: this.$store.state.currentsurvey.spotify,
      survey_normvalues_id: this.$store.state.currentsurvey.survey_normvalues_id,
      norm_values_options: [],
      surveytype_options: [],
      survey_language_id: this.$store.state.currentsurvey.survey_language_id,
      normvalues_countries: this.$store.state.normvalues_countries,
      currentcode: '',
      currentlink: '',
      selected: 0,
      fields: [
        { key: 'select', label: '', sortable: false, tdClass: 'editable', thClass: 'select-th' },
        { key: 'groupsurveymember_survey_code', sortable: true, label: 'Code', tdClass: 'd-none d-sm-table-cell', thClass: 'd-none d-sm-table-cell' },
        { key: 'groupmember_email', label: 'Email', sortable: true, class: 'editable email-column' },
        { key: 'groupsurveymember_status_id', label: 'Status', sortable: true, class: 'editable color-input' },
        // { key: 'groupmember_name', label: '[Member] name', tdClass: 'd-none d-sm-table-cell',thClass: 'd-none d-sm-table-cell'  },
        //        { key: 'groupmember_mobile', label: 'Mobile phone number', class: 'col-3 td-3' },
        { key: 'groupsurveymember_formoption_id', label: 'Form type', sortable: true, class: 'editable select-column' },
        { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
      ],
      fieldsExclude: [
        { key: 'groupsurveymember_survey_code', label: 'Code', tdClass: 'd-none d-sm-table-cell', thClass: 'd-none d-sm-table-cell' },
        { key: 'groupmember_email', label: 'Email', class: 'editable email-column' },
        { key: 'groupsurveymember_formoption_id', label: 'Form type', class: 'editable select-column' },
        { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
      ],
      original_member_data: {}, // To store the original data before editing
      numberSelectedMembersWithEmail: 0,
      canExcludeMembers: true
    }
  },
  computed: {
    plainTextInvitation () {
      return this.survey.survey_invitation_text
        .replace(/<\/p>/g, '\n\n')
        .replace(/<br\s*\/?>/g, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
    },
    currentcodelink: function () {
      return this.currentlink + '\n\n' + this.currentcode + '\n\n' + this.plainTextInvitation
    },
    includedGroupMembers () {
      return this.groupmembers_grid.filter(member => member.included_in_survey == 1)
    },
    excludedGroupMembers () {
      return this.groupmembers_grid.filter(member => member.included_in_survey == 0)
    },
    completedGroupMembers () {
      return this.includedGroupMembers.filter(member => member.groupsurveymember_status_id == 4)
    },
    normvalues_country_name: {
      get () {
        var normvalues_country_name = ''
        for (var i = 0; i < this.normvalues_countries.length; i++) {
          if (this.normvalues_countries[i].country_id == this.survey_normvalues_id) {
            normvalues_country_name = this.normvalues_countries[i].country_name
          }
        }
        return normvalues_country_name
      },
      set () {
        return true
      }
    },
    language_name: {
      get () {
        const langid = this.survey.survey_language_id
        var language_name = ''
        for (var i = 0; i < this.languages.length; i++) {
          if (this.languages[i].id == langid) {
            language_name = this.languages[i].language_name
          }
        }
        // var language_name = this.languages[this.$store.state.currentsurvey.survey_language_id];
        return language_name
      },
      set () {
        return true
      }
    },
    anon: function () {
      return this.$store.state.anon
    },
    membersselected: {
      get () {
        var m = this.$store.state.selectedsurveymembers
        return m
      },
      set () {
        return true
      }
    },
    completecount: {
      get () {
        return this.$store.state.currentsurvey.completecount
      },
      set () {
        return true
      }
    },
    membercount: {
      get () {
        return this.$store.state.currentsurvey.membercount
      },
      set () {
        return true
      }
    },
    formoptions: {
      get () {
        var items = this.$store.state.formoptions
        var formoptions = []
        for (var i = 0; i < items.length; i++) {
          var option = {
            value: items[i].id,
            text: items[i].formoption_name
          }
          formoptions.push(option)
        }
        return formoptions
      }
    },
    surveymembers_reload: function () {
      return this.$store.state.surveymembers_reload
    },
    survey_status: {
      get () {
        var status = 'launched'
        if (this.survey_status_id == 1) {
          status = 'draft'
        }
        if (
          this.survey_status_id == 2 &&
          this.survey_start_date.substring(0, 10) > this.format_date(new Date())
        ) {
          status = 'scheduled'
        }
        return status
      },
      set () {
        return true
      }
    },
    survey_id: {
      get () {
        return this.$store.state.currentsurvey.id
      },
      set () {
        return true
      }
    },
    reminder_date_min: {
      get () {
        if (!this.survey_start_date) { return null }
        let newvalue = new Date(Date.parse(this.survey_start_date))
        newvalue.setDate(newvalue.getDate() + 1)
        const thisdate = new Date()
        if (thisdate > newvalue) {
          newvalue = thisdate
          newvalue.setDate(newvalue.getDate() + 1)
        }
        return newvalue
      },
      set () {
        return true
      }
    },
    canremind: {
      get () {
        if (!this.survey_end_date) { return false }
        const nowvalue = new Date().toISOString().split('T')[0]
        const endvalue = new Date(Date.parse(this.survey_end_date)).toISOString().split('T')[0]
        return endvalue > nowvalue
      },
      set () {
        return true
      }
    }
  },
  watch: {
    surveymembers_reload: function () {
      this.surveymembers_get()
    }
  },
  created () {
    this.survey_get()
    this.surveymembers_get()
    this.group_get()
    this.languages_get()
    this.normvalues_countries_get()
    this.surveytypes_get()
    this.formoptions_get()
    this.labels_get()
    this.screentexts_get()
    this.addonsurveys_get()
    this.changelanguage(this.survey.survey_language_id)
    this.$store.dispatch('groupsurveymembers_setselected', { surveymembers: [] })
  },
  mounted () { },
  methods: {
    selectall (selected) {
      for (var i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.groupmembers_grid[i].included_in_survey == 1) {
          this.groupmembers_grid[i].selected = selected
        }
      }
      this.countselected()
    },
    countselected () {
      var selectedsurveymembers = []
      var numbersel = 0
      for (var i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.groupmembers_grid[i].included_in_survey == 1) {
          if (this.groupmembers_grid[i].selected) {
            selectedsurveymembers.push(this.groupmembers_grid[i].id)
            numbersel++
          }
        }
      }
      this.membersselected = numbersel
      this.numberSelectedMembersWithEmail = this.groupmembers_grid.filter(
        member => member.groupmember_email != '' &&
        member.groupsurveymember_formoption_id == 1 &&
        member.selected
      ).length
      this.$store.dispatch('groupsurveymembers_setselected', { surveymembers: selectedsurveymembers })
    },
    toggleEditable () {
      this.editable = !this.editable
      const rows = document.querySelectorAll('.table-alternative tr')
      rows.forEach(row => {
        if (this.editable) {
          row.classList.add('editable-row')
        } else {
          row.classList.remove('editable-row')
          this.saveform()
        }
      })
    },
    getlink (id) {
      this.$store.dispatch('groupsurveymember_fetch', { id: id })
        .then(resp => {
          const link = config.front_url + '/gdqquestionnaire/' + resp.data.groupsurveymember_token
          this.currentlink = link
          this.currentcode = resp.data.groupsurveymember_survey_code
          this.$bvModal.show('memberlink')
        })
    },
    printform (id) {
      this.$store.dispatch('paperform_fetch', { surveymember_id: id })
        .then((resp) => {
          var paperformsurl = config.report_url + '/gdq_paperforms.php?i=' + resp.data.id
          window.open(paperformsurl)
        })
    },
    rowClass (item, index) {
      return item.editable ? 'editable-row' : ''
    },
    formoptions_get: function () {
      this.$store.dispatch('formoptions_fetch')
    },
    getFormOptionText (id) {
      const option = this.formoptions.find(option => option.value === id)
      return option ? option.text : ''
    },
    editGroupMember (id) {
      for (let i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.groupmembers_grid[i].id === id) {
          if (this.groupmembers_grid[i].editable) {
            this.groupmembers_grid[i].editable = false
          } else {
            // Cancel edit for all other rows
            this.groupmembers_grid.forEach((member, index) => {
              if (index !== i) {
                this.cancelEdit(member.id)
              }
            })
            this.groupmembers_grid[i].editable = true
            this.original_member_data[id] = JSON.parse(JSON.stringify(this.groupmembers_grid[i]))
          }
        }
      }
    },
    cancelEdit (id) {
      const index = this.groupmembers_grid.findIndex(member => member.id === id)
      if (index !== -1 && this.original_member_data[id]) {
        this.groupmembers_grid[index].groupsurveymember_survey_code = this.original_member_data[id].groupsurveymember_survey_code
        this.groupmembers_grid[index].groupmember_email = this.original_member_data[id].groupmember_email
        this.groupmembers_grid[index].groupmember_mobile = this.original_member_data[id].groupmember_mobile
        this.groupmembers_grid[index].groupmember_formoption_id = this.original_member_data[id].groupmember_formoption_id
        this.groupmembers_grid[index].groupsurveymember_formoption_id = this.original_member_data[id].groupsurveymember_formoption_id
        this.groupmembers_grid[index].groupmember_status_id = this.original_member_data[id].groupmember_status_id
        this.groupmembers_grid[index].included_in_survey = this.original_member_data[id].included_in_survey
        this.groupmembers_grid[index].editable = false
        delete this.original_member_data[id]
      }
    },
    saveEdit (id) {
      const index = this.groupmembers_grid.findIndex(member => member.id === id)
      if (index !== -1) {
        const member = this.groupmembers_grid[index]
        const original = this.original_member_data[id]
        const originalEmail = original.groupmember_email || ''
        if (this.validateEditedMember(member)) {
          if (member.groupmember_email != originalEmail) {
            member.groupsurveymember_status_id = 1
            this.groupmembers_grid[index].groupsurveymember_status_id = 1
          }
          member.editable = false
          delete this.original_member_data[id]
          this.updatesurvey(false)
        } else {
          member.groupmember_email_valid = 'invalid'
          const mail = member.groupmember_email
          member.groupmember_email = ''
          member.groupmember_email = mail
        }
      }
    },
    validateEditedMember (member) {
      if (member.groupsurveymember_formoption_id != 1) {
        member.groupmember_email_valid = null
        member.groupmember_email = ''
        return true
      } else {
        return this.parse_email(member.groupmember_email)
      }
    },

    parse_email (email) {
      // eslint-disable-next-line
      const pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return pattern.test(email.toLowerCase())
    },

    languages_get () {
      this.$store.dispatch('languages_fetch')
        .then((res) => {
          this.languages = res.data.result
        })
    },

    surveytype_get () {
      this.$store.dispatch('record_read', { table_name: 'g_surveytypes', id: this.survey_type_id })
        .then((resp) => {
          this.surveytype_name = resp.data.surveytype_name
        })
    },

    addonsurveys_get () {
      this.$store.dispatch('record_readlist', { table_name: 'g_addon_surveys', orderby: 'survey_name' })
        .then((resp) => {
          const result = resp.data.result
          var options = new Array()
          options.push({ value: 0, text: '-- No add-on survey --' })
          for (var i = 0; i < result.length; i++) {
            options.push(
              { value: result[i].id, text: result[i].survey_name }
            )
          }
          this.addonsurveys = options
        })
    },
    language_options_get () {
      const survey_type_id = this.survey_type_id
      var filter = {}
      switch (survey_type_id) {
        case '1':
          filter['language_enabled'] = {
            operator: '=',
            value: '1'
          }
          break
        case '2':
          filter['language_enabled_gdq_short'] = {
            operator: '=',
            value: '1'
          }
          break
        case '3':
          filter['language_enabled_osc'] = {
            operator: '=',
            value: '1'
          }
          break
        default:
          filter['language_enabled'] = {
            operator: '=',
            value: '1'
          }
      }
      this.$store.dispatch('records_readall', { table_name: 'g_languages', filter: filter, orderby: 'language_name' })
        .then((resp) => {
          this.language_options = []
          for (var i = 0; i < resp.data.result.length; i++) {
            this.language_options.push({
              value: resp.data.result[i].id,
              text: resp.data.result[i].language_name
            })
          }
        })
      return true
    },
    openquestionnaire (id) {
      this.$store.dispatch('groupsurveymember_fetch', { id: id })
        .then((resp) => {
          var token = resp.data.groupsurveymember_token
          var code = resp.data.groupsurveymember_survey_code
          this.$store.dispatch('questionnaire_setadmin', { admin: true })
            .then(() => {
              this.$store.dispatch('questionnaire_setadmin_code', { code: code })
                .then(() => {
                  window.open('/gdqquestionnaire/' + token)
                })
            })
        })
    },
    admin_excluded: function () {
      this.excludablequestions_get()
      this.administer_excluded = !this.administer_excluded
      if (this.administer_excluded) {
        this.administer_mailtext = false
        this.administer_reimport = false
        this.administer_addon = false
      }
    },
    excludablequestions_get () {
      this.excludablequestions = []
      this.$store.dispatch('excludedquestions_fetch', { survey_id: this.survey_id })
        .then((resp) => {
          this.excludablequestions = resp.data.questions
          for (var i = 0; i < resp.data.questions.length; i++) {
            if (resp.data.questions[i].excluded == 1) {
              this.excludedquestions.push(resp.data.questions[i].generalquestion_id)
            }
          }
        })
    },
    async admin_excluded_save () {
      await this.$store.dispatch('excludedquestions_save', { survey_id: this.survey_id, questions: this.excludedquestions })
        .then(() => {
          this.administer_excluded = false
        })
    },
    admin_mailtext: function () {
      this.administer_mailtext = !this.administer_mailtext
      if (this.administer_mailtext) {
        this.administer_excluded = false
        this.administer_reimport = false
        this.administer_addon = false
      }
    },
    admin_addon: function () {
      this.administer_addon = !this.administer_addon
      if (this.administer_mailtext) {
        this.administer_excluded = false
        this.administer_reimport = false
        this.administer_mailtext = false
      }
    },
    admin_mailtext_save () {
      var data = {
        id: this.$store.state.currentsurvey.id,
        survey_invitation_subject: this.survey_invitation_subject,
        survey_invitation_consultant_text: this.survey_invitation_consultant_text
      }
      this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
        this.survey_get()
        this.administer_mailtext = !this.administer_mailtext
      })
    },
    admin_addon_save () {
      return true
    },
    admin_reimport: function () {
      this.reimporting = -1
      this.administer_reimport = !this.administer_reimport
      if (this.administer_reimport) {
        this.administer_excluded = false
        this.administer_mailtext = false
        this.administer_addon = false
      }
    },
    reimport: async function () {
      const surveymembers = this.$store.state.currentsurveymembers
      this.reimported = []
      this.reimportstatus = 0
      for (let i = 0; i < surveymembers.length; i++) {
        this.reimporting = i + 1
        const m = surveymembers[i]
        const data = { surveymember_id: m.id }
        await this.$store.dispatch('legacyanswers_get', data)
          .then(resp => {
            if (resp.data.old_id === '0') {
              this.reimportstatus = 2
            }
          }
          )
      }
      if (this.reimportstatus == 0) { this.reimportstatus = 1 }
      this.survey_get()
      this.reimporting = -1
    },
    screentexts_get: function () {
      var screentexts = []
      var filter = {
        screen_id: {
          operator: '=',
          value: this.screen_id
        }
      }
      this.$store
        .dispatch('systemscreenelements_fetch', { filter: filter })
        .then(resp => {
          var dbtexts = resp.data.result
          for (var i = 0; i < dbtexts.length; i++) {
            var screentext = {
              id: dbtexts[i].id,
              element_text: dbtexts[i].element_text,
              element_bubbletext: dbtexts[i].element_bubbletext
            }
            screentexts[dbtexts[i].id] = screentext
          }
          this.screentexts = screentexts
        })
    },
    print_paperforms () {
      this.$store
        .dispatch('paperforms_fetch', {
          survey_id: this.$store.state.currentsurvey.id
        })
        .then(resp => {
          var paperformsurl =
            config.report_url + '/gdq_paperforms.php?i=' + resp.data.id
          window.open(paperformsurl)
        })
    },
    groupsurvey_report () {
      this.$router.push('/group/survey/report')
    },
    async groupsurvey_reopen () {
      var data = {
        id: this.$store.state.currentsurvey.id,
        survey_status_id: 3
      }
      this.formchanged = false
      this.survey.survey_status_id = 3
      await this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
        this.survey_get()
      })
    },
    groupsurvey_close_ask () {
      this.confirmAnonymize = false
      this.wanttoclose = true
    },
    groupsurvey_close_cancel () {
      this.wanttoclose = false
    },
    async groupsurvey_close () {
      if (this.anonymize_survey_on_close == 1) {
        await this.$store.dispatch('groupsurvey_anonymize', { survey_id: this.$store.state.currentsurvey.id })
      }
      this.$store
        .dispatch('groupsurvey_close', {
          survey: this.$store.state.currentsurvey
        })
        .then(() => {
          this.wanttoclose = false
          this.survey_get()
          this.$store.dispatch('surveymembers_setreload', { reload: 1 })
        })
    },
    cetdate_get: async function () {
      const resp =  await this.$store.dispatch('serverdate_fetch')
      const thedate = resp.data
      console.log
      return thedate
    },
    async groupsurvey_launch () {
      this.survey_status_id = 3
      var mailrecipients = []
      for (var i = 0; i < this.$store.state.currentsurveymembers.length; i++) {
        if (this.$store.state.currentsurveymembers[i].groupsurveymember_formoption_id == 1) {
          mailrecipients.push(this.$store.state.currentsurveymembers[i])
        }
      }
      var payload = {
        survey: this.$store.state.currentsurvey,
        surveymembers: mailrecipients,
        mail_subject: this.$store.state.currentsurvey.survey_invitation_subject,
        mail_text: this.$store.state.currentsurvey.survey_invitation_text,
        mail_consultant_text: this.mail_consultant_text,
        mail_cc: this.$store.state.currentsurvey.survey_invitation_cc,
        mail_type: 'Invitation'
      }
      await this.$store.dispatch('groupsurvey_mail', payload)
      const serverdate = await this.cetdate_get()
      console.log('Server date:', serverdate)
      var data = {
        id: this.$store.state.currentsurvey.id,
        survey_status_id: 3,
        survey_start_date: serverdate
      }
      console.log('Saving with data:',{data} )
      this.formchanged = false
      await this.$store.dispatch('groupsurvey_save', { survey: data })
      const dbsurvey = await this.survey_get()
      // console.log({ dbsurvey })
      this.survey = dbsurvey;
      await this.$store.dispatch('surveymembers_setreload', { reload: 1 })
    },
    checkreadyforlaunch () {
      var ready = true
      // No members - cannot launch
      if (this.membercount == 0) {
        ready = false
      }
      // End date - cannot launch
      if (
        this.survey_end_date == null ||
        this.survey_end_date == '' ||
        this.survey_end_date < this.survey_start_date
      ) {
        ready = false
      }
      // Here I am not sure, I don't think we should set the start date
      if (
        (this.survey_start_date == null || this.survey_start_date == '') &&
        ready == true
      ) {
        ready = false
      }
      if (ready) {
        var data = {
          id: this.$store.state.currentsurvey.id,
          survey_status_id: 2
        }
        this.formchanged = false
        this.$store.dispatch('groupsurvey_save', { survey: data }).then(() => {
          this.survey_get()
        })
      }
    },
    changelanguage (survey_language_id) {
      // Copy the mail text for the new language
      this.$store
        .dispatch('groupsurvey_changelanguage', {
          survey_id: this.$store.state.currentsurvey.id,
          survey_language_id: survey_language_id
        })
        .then(() => {
          // Then save the molevitt
          this.savefield('survey_language_id', survey_language_id)
        })
    },
    savefield (fieldname, value) {
      if (!this.loading) {
        if (fieldname == 'survey_reminder') {
          if (!value) {
            value = 0
          }
        }
        if (fieldname.indexOf('_date') != -1) {
          value = this.format_date(value)
        }
        if (fieldname.indexOf('_time') != -1) {
          if (value != null && value != '') {
            value = this.format_time(value)
          } else {
            value = '00:00'
          }
        }
        if (this.validateform()) {
          var data = {
            id: this.$store.state.currentsurvey.id,
            [fieldname]: value
          }
          this.formchanged = false
          this.$store
            .dispatch('groupsurvey_save', { survey: data })
            .then(() => {
              if (this.survey_status_id == 1) {
                this.checkreadyforlaunch()
              }
              if (fieldname == 'survey_start_date') {
                this.surveychanged = !this.surveychanged
              }
            })
            .then(() => {
              this.survey_get()
            })
        }
      }
    },
    validateform () {
      var result = true
      if (this.survey_name == '') {
        this.survey_name_valid = false
        result = false
      } else {
        this.survey_name_valid = true
      }
      if (this.survey_normvalues_id == 0) {
        this.survey_normvalues_id_valid = false
        result = false
      }
      return result
    },
    saveform () {
      if (!this.saving) {
        this.saving = true
        this.formchanged = false
        var data = {
          id: this.$store.state.currentsurvey.id,
          survey_name: this.survey.survey_name,
          survey_invoice_reference: this.survey_invoice_reference,
          survey_normvalues_id: this.survey_normvalues_id,
          survey_language_id: this.survey.survey_language_id,
          survey_start_date: this.format_date(this.survey.survey_start_date),
          survey_start_time: this.format_time(this.survey.survey_start_time),
          survey_end_date: this.format_date(this.survey.survey_end_date),
          survey_reminder_date: this.format_date(this.survey_reminder_date),
          survey_reminder_time: this.format_time(this.survey_reminder_time),
          survey_reminder: this.survey_reminder,
          survey_reminder_email: this.survey_reminder_email,
          labels: [],
          addinglabel: 0,
          addinglabeltext: 0
        }
        this.$store.dispatch('groupsurvey_save', { survey: data })
          .then(() => {
            this.surveychanged = !this.surveychanged
            this.survey_get()
            this.saving = false
          })
      }
    },
    async updatesurvey (adding) {
      this.addingMember = true;
      try {
        var newmember = {
          newid: 0,
          id: this.nextid,
          groupmember_email: this.groupmember_email,
          groupmember_mobile: this.groupmember_mobile,
          groupmember_name: this.groupmember_name,
          groupmember_formoption_id: this.groupmember_formoption_id,
          groupsurveymember_formoption_id: this.groupmember_formoption_id,
          included_in_survey: 1
        }
        if (newmember != null && adding) {
          this.nextid++
          this.cleargroupmember()
          this.groupmembers_grid.push(newmember)
          var data = {
            group_id: this.$store.state.currentgroup_id,
            survey_id: this.$store.state.currentsurvey.id,
            groupmembers: this.groupmembers_grid,
            group: this.$store.state.currentgroup,
            survey: this.$store.state.currentsurvey
          }
          this.cleargroupmember()
          this.$store.dispatch('addingsurveymember_set', {
            addingsurveymember: true
          })
          await this.$store.dispatch('groupsurveymembers_create', data)
          this.surveymembers_get()
        } else {
          var savemembers = []
          for (var i = 0; i < this.groupmembers_grid.length; i++) {
            var member = {
              newid: this.groupmembers_grid[i].id,
              id: this.groupmembers_grid[i].groupmember_id,
              groupmember_email: this.groupmembers_grid[i].groupmember_email,
              groupmember_name: this.groupmembers_grid[i].groupmember_name,
              groupmember_mobile: this.groupmembers_grid[i].groupmember_mobile,
              groupsurveymember_formoption_id: this.groupmembers_grid[i].groupsurveymember_formoption_id,
              groupsurveymember_status_id: this.groupmembers_grid[i].groupsurveymember_status_id,
              included_in_survey: this.groupmembers_grid[i].included_in_survey
            }
            savemembers.push(member)
          }
          var data = {
            group_id: this.$store.state.currentgroup_id,
            survey_id: this.$store.state.currentsurvey.id,
            groupmembers: savemembers,
            group: this.$store.state.currentgroup,
            survey: this.$store.state.currentsurvey
          }
          this.cleargroupmember()
          await this.$store.dispatch('groupsurveymembers_create', data)
          this.surveymembers_get()
        }
      } finally {
        this.addingMember = false;
      }
    },
    clearform () {
      this.formchanged = false
      this.survey_get()
    },
    setformchanged () {
      if (!this.loading) {
        this.formchanged = true
      }
      return true
    },
    checkstartdate (event) {
      if (this.loading == true) { return true }
      this.showtimeadjust = false
      const startvalue = this.survey.survey_start_date.substring(0, 10) + 'T' + this.survey.survey_start_time.substr(this.survey.survey_start_time.indexOf('T') + 1)
      const startvaluetz = new Date((typeof startvalue === 'string' ? new Date(startvalue) : startvalue).toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }))
      const nowdate = new Date()
      if (startvaluetz < nowdate) {
        this.showtimeadjust = true
      }
    },
    togglegroupmember (id) {
      for (var i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.groupmembers_grid[i].id == id) {
          if (this.groupmembers_grid[i].included_in_survey == 1) {
            this.groupmembers_grid[i].included_in_survey = 0
          } else {
            this.groupmembers_grid[i].included_in_survey = 1
          }
          this.updatesurvey(false)
        }
      }
    },
    cleargroupmember () {
      this.groupmember_name = ''
      this.groupmember_email = ''
      this.groupmember_mobile = ''
    },
    validategroupmember () {
      var formok = true
      this.groupmember_name_valid = null
      this.groupmember_email_valid = null
      this.groupmember_mobile_valid = null
      // Online form
      if (this.groupmember_formoption_id == 1) {
        if (this.groupmember_email == '') {
          this.groupmember_email_valid = 'invalid'
          formok = false
        }
        // eslint-disable-next-line
        // if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(this.groupmember_email) == false) {
        if (!this.parse_email(this.groupmember_email)) {
          this.groupmember_email_valid = 'invalid'
          formok = false
        }
      }
      return formok
    },
    addgroupmember () {
      if (this.validategroupmember()) {
        this.updatesurvey(true)
      }
    },
    formoptions_get: function () {
      this.$store.dispatch('formoptions_fetch')
    },
    toggleaddmember () {
      this.cleargroupmember()
      this.addmember = !this.addmember
      this.contactmembers = false
      this.sendinvitations = false
    },
    togglecontactmembers () {
      this.contactmembers = !this.contactmembers
      this.sendinvitations = false
      if (this.addmember) {
        this.cleargroupmember()
      }
      this.addmember = false
    },
    togglesendinvitations () {
      this.sendinvitations = !this.sendinvitations
      this.contactmembers = false
      if (this.addmember) {
        this.cleargroupmember()
      }
      this.addmember = false
    },
    toggleSelected (id, scrollto, type) {
      for (var i = 0; i < this.groupmembers_grid.length; i++) {
        if (this.groupmembers_grid[i].id == id) {
          this.groupmembers_grid[i].selected = true
        } else {
          this.groupmembers_grid[i].selected = false
        }
      }
      if (type == 'invite') {
        this.sendinvitations = true
        this.contactmembers = false
        if (this.addmember) {
          this.cleargroupmember()
        }
        this.addmember = false
      } else if (type == 'contact') {
        this.contactmembers = true
        this.sendinvitations = false
        if (this.addmember) {
          this.cleargroupmember()
        }
        this.addmember = false
      }
      this.countselected()

      this.$nextTick(() => {
        setTimeout(() => {
          const element = document.getElementById(scrollto)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      })
    },
    sendemails (type) {
      var mailrecipients = []
      var send_mail_subject = ''
      var send_mail_text = ''
      var send_mail_consultant_text = ''
      var send_mail_cc = ''
      if (type == 'Invitation') {
        send_mail_subject = this.survey_invitation_subject
        send_mail_text = this.survey_invitation_text
        send_mail_consultant_text = this.survey_invitation_consultant_text
        send_mail_cc = this.survey_invitation_cc
      }
      if (type == 'Other') {
        send_mail_subject = this.mail_subject
        send_mail_text = this.mail_consultant_text
        send_mail_consultant_text = this.mail_consultant_text
        send_mail_cc = ''
      }
      for (var i = 0; i < this.$store.state.currentsurveymembers.length; i++) {
        if (this.membersselected.includes(this.$store.state.currentsurveymembers[i].id)) {
          if (this.$store.state.currentsurveymembers[i].groupsurveymember_formoption_id == 1) {
            mailrecipients.push(this.$store.state.currentsurveymembers[i])
          }
        }
      }
      var payload = {
        survey: this.$store.state.currentsurvey,
        surveymembers: mailrecipients,
        mail_subject: send_mail_subject,
        mail_text: send_mail_text,
        mail_consultant_text: send_mail_consultant_text,
        mail_cc: send_mail_cc,
        mail_type: type
      }
      this.contactmembers = false
      this.sendinvitations = false
      this.$store.dispatch('groupsurvey_mail', payload)
      // -------------------------------------------------------------------
      // Since the mail is done "detached", we need some timing thingy here
      // -------------------------------------------------------------------
      setTimeout(
        function () {
          this.survey_get()
          this.$store.dispatch('surveymembers_setreload', { reload: 1 })
        }.bind(this),
        1500
      )
    },
    surveymembers_get () {
      this.$store.dispatch('groupsurveyandgroupmembers_fetch', { survey_id: this.$store.state.currentsurvey.id })
        .then((resp) => {
          var groupsurveymembers = new Array()
          for (var i = 0; i < resp.data.result.length; i++) {
            var groupsurveymember_formoption_id = resp.data.result[i].groupsurveymember_formoption_id
            var groupsurveymember_formoption_name = 'None'
            for (var j = 0; j < this.formoptions.length; j++) {
              if (resp.data.result[i].groupsurveymember_formoption_id == this.formoptions[j].value) {
                groupsurveymember_formoption_name = this.formoptions[j].text
              }
            }
            groupsurveymembers.push({
              id: resp.data.result[i].id,
              groupmember_id: resp.data.result[i].groupmember_id,
              groupsurveymember_survey_code: resp.data.result[i].groupsurveymember_survey_code,
              groupmember_name: resp.data.result[i].groupmember_name,
              groupmember_email: resp.data.result[i].groupmember_email,
              groupmember_mobile: resp.data.result[i].groupmember_mobile,
              groupsurveymember_formoption_id: groupsurveymember_formoption_id,
              groupsurveymember_formoption_name: groupsurveymember_formoption_name,
              groupsurveymember_status_id: resp.data.result[i].groupsurveymember_status_id,
              gdq_answers: resp.data.result[i].gdq_answers,
              included_in_survey: resp.data.result[i].included_in_survey,
              selected: 0,
              editable: false
            })
          }
          this.groupmembers_grid = groupsurveymembers
        })
    },
    survey_get: async function () {
      this.loading = true
      const res = await this.$store
        .dispatch('groupsurvey_fetch', { id: this.survey_id })
        .then((res) => {
          this.$store.commit('survey_set', res.data)
          this.group_name = this.$store.state.currentgroup.group_name
          this.survey_name = res.data.survey_name
          this.survey_start_date = res.data.survey_start_date + 'T00:00:00.00Z'
          if (
            res.data.survey_start_date > '2000-01-01'
          ) {
            this.survey_start_date =
              this.format_date(
                res.data.survey_start_date
              ) + 'T00:00:00.00Z'
            this.survey_start_time = res.data.survey_start_time
          }
          if (res.data.survey_end_date > '2000-01-01') {
            this.survey_end_date =
              this.format_date(
                res.data.survey_end_date
              ) + 'T00:00:00.00Z'
          }
          this.survey_reminder = res.data.survey_reminder
          if (
            res.data.survey_reminder_date > '2000-01-01'
          ) {
            this.survey_reminder_date =
              this.format_date(
                res.data.survey_reminder_date
              ) + 'T00:00:00.00Z'
            this.survey_reminder_time = res.data.survey_reminder_time
          }
          this.survey_reminder_email = res.data.survey_reminder_email
          this.survey_language_id = res.data.survey_language_id
          this.survey_normvalues_id = res.data.survey_normvalues_id
          this.survey_type_id = res.data.survey_type_id
          this.survey_status_id = res.data.survey_status_id
          this.survey_invoice_reference = res.data.survey_invoice_reference
          this.survey_invitation_subject = res.data.survey_invitation_subject
          this.survey_invitation_text = res.data.survey_invitation_text
          this.mail_consultant_text = res.data.survey_invitation_consultant_text
          this.survey_invitation_cc = res.data.survey_invitation_cc
          this.spotify = res.data.spotify
          this.addon_survey = res.data.addon_survey

          var members = this.$store.state.currentsurveymembers
          for (var i = 0; i < members.length; i++) {
            if (members[i].groupsurveymember_formoption_id == 1) {
              this.has_mail = 1
            }
            if (members[i].groupsurveymember_formoption_id == 2) {
              this.has_paper = 1
            }
          }
          this.$store.dispatch('record_read', { table_name: 'g_surveytypes', id: this.survey_type_id })
            .then((resp) => {
              this.surveytype_name = resp.data.surveytype_name
            })
            .then(() => {
              this.language_options_get()
            })
            .then(() => {
              this.normvalues_countries_get()
            })
          // .then(() => {
          //   if (this.spotify == 1) {
          //     this.changelanguage(this.survey_language_id);
          //   }
          // })
          const filter = {
            survey_id: {
              operator: '=',
              value: this.survey_id
            }
          }
          this.$store.dispatch('records_readall', { table_name: 'g_gdqreports', filter })
            .then((resp) => {
              if (resp.data.result.length) {
                this.canExcludeMembers = false
              }
            })
          this.loading = false
          return res.data;
        })
      // console.log({ surveygetres: res })
      return res;
    },
    group_get: function () {
      this.$store
        .dispatch('group_fetch', { id: this.$store.state.currentgroup_id })
        .then(() => {
          this.group_name = this.$store.state.currentgroup.group_name
        })
    },

    normvalues_countries_get: function () {
      let survey_type_id = this.survey_type_id
      this.$store.dispatch('normvalues_countries_fetch')
        .then((resp) => {
          var options = []
          options.push({ value: 0, text: 'Please select norm values' })
          for (var i = 0; i < resp.data.length; i++) {
            if (resp.data[i].country_id == 999 || survey_type_id == 1) {
              var option = {
                value: resp.data[i].country_id,
                text: resp.data[i].country_name
              }
              options.push(option)
            }
          }
          this.norm_values_options = options
        })
    },
    surveytypes_get: function () {
      this.$store.dispatch('surveytypes_fetch')
    },
    //= ======
    // LABELS
    //= ======
    labels_get () {
      this.$store
        .dispatch('groupsurveylabels_fetch', { survey_id: this.survey_id })
        .then(resp => {
          this.labels = resp.data
        })
    },
    saveaddlabel () {
      if (this.addinglabeltext != '') {
        var savelabel = {
          survey_id: this.addinglabel,
          label: this.addinglabeltext
        }
        this.$store
          .dispatch('groupsurveylabel_add', { label: savelabel })
          .then(() => {
            this.labels_get()
          })
      }
      this.addinglabel = 0
      this.addinglabeltext = ''
    },
    addlabel (id) {
      this.addinglabel = id
      this.addinglabeltext = ''
      this.$nextTick(() => this.$refs.addinglabel.focus())
    },
    removelabel (groupsurveylabel_id) {
      this.$store
        .dispatch('groupsurveylabel_delete', { id: groupsurveylabel_id })
        .then(() => {
          this.labels_get()
        })
    },
    //= ================
    // HELPER FUNCTIONS
    //= ================
    format_date (date) {
      var d = new Date(date)
      var month = '' + (d.getMonth() + 1)
      var day = '' + d.getDate()
      var year = d.getFullYear()
      if (isNaN(year)) {
        return ''
      }
      if (Number.isNaN(month)) {
        return ''
      }
      if (month.length < 2) month = '0' + month
      if (day.length < 2) day = '0' + day
      return [year, month, day].join('-')
    },
    format_time (date) {
      if (date == null || date == undefined) {
        return ''
      }
      if (date.length == 5) {
        return date
      }
      var d = new Date(date)
      var h = d.getHours()
      var m = d.getMinutes()
      var hour = (h > 9 ? '' : '0') + h
      var minute = (m > 9 ? '' : '0') + m
      var thistime = hour + ':' + minute
      return thistime
    },
    copyText () {
      const editorContent = document.querySelector('#fixed-text')
      const range = document.createRange()
      range.selectNodeContents(editorContent)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      document.execCommand('copy')
      selection.removeAllRanges()
    }
  }
}
</script>
<style scoped>
.gdq-strike {
  text-decoration: line-through;
}
</style>
